import { useQueryClient } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import useAssets from '~hooks/useAssets';
import { fetchSearchResults } from '~modules/media-library/controller/api/search';
import { type Asset, type AssetId } from '~modules/media-library/model/Asset';
import { makeSearchResult } from '~modules/media-library/model/SearchResultFactory';
import { getLastItem } from '~utils/array';

export default function useSearch(realm: string, query: string, idCollection?: AssetId) {
  const { setAssets } = useAssets();
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => [{ realm, query, idCollection }], [realm, query, idCollection]);

  const { data, isLoading, isError, fetchNextPage } = useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ pageParam }) => {
      return makeSearchResult(await fetchSearchResults(realm, query, idCollection, pageParam as number));
    },
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: ({ start, rows, numFound }) => {
      //if (lastPage === undefined) return 1;
      const idxResults: number = start + rows;
      // exit when last result reached
      if (idxResults >= numFound) return undefined;
      // otherwise return new start index
      return idxResults;
    },
  });
  useEffect(() => {
    if (data === undefined) return;
    const latestPage = getLastItem(data.pages);
    if (latestPage.assets === undefined) return;
    setAssets(latestPage.assets);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return useMemo(
    () => ({
      resultAssetIds: data?.pages.reduce<Asset[]>((prev, curr) => prev.concat(curr.assets), []).map(asset => asset.id),
      resultCount: data?.pages[0]?.numFound,
      isLoading,
      isError,
      onLoadMore: () => {
        fetchNextPage();
      },
      resetSearchResult: () => {
        queryClient.setQueryData<{
          pages: any[];
          pageParams: any[];
        }>(queryKey, oldData => {
          if (!oldData) return undefined;
          return {
            pages: oldData.pages.slice(0, 1),
            pageParams: oldData.pageParams.slice(0, 1),
          };
        });
        queryClient.invalidateQueries();
      },
    }),
    [data?.pages, isLoading, isError, fetchNextPage, queryClient, queryKey],
  );
}
